import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  signal,
  input,
  output,
  inject,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { TranslateModule } from '@ngx-translate/core';
import { PopupComponent } from '../../../components/popup/popup.component';
import { PopupService } from '../../../services/popup.service';
import { ScreenSpinnerComponent } from '../../../components/screen-spinner/screen-spinner.component';
import { ScreenSpinnerService } from '../../../services/screen-spinner.service';

@Component({
  selector: 'lib-custom-cropper',
  standalone: true,
  imports: [
    CommonModule,
    ImageCropperComponent,
    TranslateModule,
    PopupComponent,
    ScreenSpinnerComponent,
  ],
  templateUrl: './custom-cropper.component.html',
})
export class CustomCropperComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ImageCropperComponent) imageCropper!: ImageCropperComponent;
  @ViewChild('cropperContainer') cropperContainer!: ElementRef;

  // Modal services
  private _popupService: PopupService = inject(PopupService);
  private _spinnerService: ScreenSpinnerService = inject(ScreenSpinnerService);

  // Modal properties
  modalId = input<string>('custom-cropper-modal');
  loading = signal<boolean>(false);

  /**
   * El archivo a procesar
   */
  file = input<File | null>(null);
  getFile(): File | null {
    return this.file();
  }

  /**
   * Relación de aspecto para el recorte (ancho/alto)
   */
  aspectRatio = input<number>(1);
  getAspectRatio(): number {
    return Number(this.aspectRatio());
  }

  /**
   * Mantener la relación de aspecto
   */
  maintainAspectRatio = input<boolean>(true);

  /**
   * Ancho al que se redimensionará la imagen recortada
   */
  resizeToWidth = input<number>(800);
  // Devuelve el valor como un número primitivo, no como un objeto Number
  getResizeToWidth(): number {
    return Number(this.resizeToWidth());
  }

  /**
   * Evento que se emite cuando se completa el recorte
   */
  croppedFile = output<File>();

  /**
   * Evento que se emite cuando se cancela el recorte
   */
  canceled = output<void>();

  /**
   * Evento que se emite cuando se cierra el modal
   */
  closePopup = output<'success' | 'close'>();

  /**
   * Datos de la imagen recortada
   */
  private _croppedImage = signal<string | null>(null);
  getCroppedImage(): string | null {
    return this._croppedImage();
  }

  /**
   * Transformaciones de la imagen
   */
  private _transform = signal<ImageTransform>({});

  /**
   * Variable para mantener la imagen dentro de la relación de aspecto
   */
  private _containWithinAspectRatio = signal<boolean>(true);

  ngOnInit(): void {
    // Crear un estilo global para la altura fija del cropper y el modo contain
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-height {
        height: 400px;
      }
      .contain-mode {
        position: relative;
      }
      .contain-mode::after {
        content: 'Contain Mode';
        position: absolute;
        top: 5px;
        right: 5px;
        color: white;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 10px;
        z-index: 10;
      }
    `;
    document.head.appendChild(style);
  }

  ngAfterViewInit(): void {
    // Abrir el modal cuando el componente esté listo
    this._popupService.open(this.modalId(), 'Recortar imagen', 'lg');
  }

  ngOnDestroy(): void {
    // Asegurarse de cerrar el modal si el componente se destruye
    this._popupService.close(this.modalId());
  }

  /**
   * Se llama cuando el recortador está listo
   */
  cropperReady(): void {
    console.log('Cropper ready');
  }

  /**
   * Se llama cuando falla la carga de la imagen
   */
  loadImageFailed(): void {
    console.error('La carga de la imagen falló');
  }

  /**
   * Maneja el evento de imagen recortada
   */
  imageCropped(event: ImageCroppedEvent): void {
    console.log('Imagen recortada:', event);

    // Si tenemos base64, lo usamos directamente
    if (event.base64) {
      this._croppedImage.set(event.base64);
      return;
    }

    // Si tenemos un blob, lo convertimos a base64
    if (event.blob) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        this._croppedImage.set(base64);
      };
      reader.readAsDataURL(event.blob);
      return;
    }

    // Si no hay ni base64 ni blob, establecemos null
    this._croppedImage.set(null);
  }

  /**
   * Rota la imagen hacia la izquierda
   */
  rotateLeft(): void {
    const currentTransform = this._transform();
    const currentRotation = currentTransform.rotate || 0;
    this._transform.set({
      ...currentTransform,
      rotate: currentRotation - 90,
    });
    this._flipAfterRotate();
  }

  /**
   * Rota la imagen hacia la derecha
   */
  rotateRight(): void {
    const currentTransform = this._transform();
    const currentRotation = currentTransform.rotate || 0;
    this._transform.set({
      ...currentTransform,
      rotate: currentRotation + 90,
    });
    this._flipAfterRotate();
  }

  /**
   * Ajusta el flip después de rotar para mantener la orientación correcta
   */
  private _flipAfterRotate(): void {
    const currentTransform = this._transform();
    const flippedH = currentTransform.flipH;
    const flippedV = currentTransform.flipV;
    this._transform.set({
      ...currentTransform,
      flipH: flippedV,
      flipV: flippedH,
    });
  }

  /**
   * Voltea la imagen horizontalmente
   */
  flipHorizontal(): void {
    const currentTransform = this._transform();
    this._transform.set({
      ...currentTransform,
      flipH: !currentTransform.flipH,
    });
  }

  /**
   * Voltea la imagen verticalmente
   */
  flipVertical(): void {
    const currentTransform = this._transform();
    this._transform.set({
      ...currentTransform,
      flipV: !currentTransform.flipV,
    });
  }

  /**
   * Alterna entre mantener la imagen dentro de la relación de aspecto
   * Cuando está activado, la imagen se ajusta dentro de la relación de aspecto (como object-fit: contain)
   * Cuando está desactivado, la imagen puede extenderse más allá de la relación de aspecto
   */
  toggleContainWithinAspectRatio(): void {
    // Invertir el valor actual
    const newValue = !this._containWithinAspectRatio();
    this._containWithinAspectRatio.set(newValue);
    // Proporcionar retroalimentación visual
    console.log(`Contain within aspect ratio: ${newValue ? 'activado' : 'desactivado'}`);
    // Si tenemos acceso al contenedor del cropper, podemos añadir una clase visual
    if (this.cropperContainer) {
      const element = this.cropperContainer.nativeElement;
      if (newValue) {
        element.classList.add('contain-mode');
      } else {
        element.classList.remove('contain-mode');
      }
    }
  }

  /**
   * Obtiene el valor de containWithinAspectRatio
   */
  getContainWithinAspectRatio(): boolean {
    return Boolean(this._containWithinAspectRatio());
  }

  /**
   * Devuelve el transform actual
   */
  getTransform(): ImageTransform {
    return { ...this._transform() };
  }

  /**
   * Aplica el recorte y emite el archivo resultante
   */
  applyCrop(): void {
    console.log('Aplicando recorte');
    // Asegurarnos de obtener la imagen recortada actual
    const currentCroppedImage = this.getCroppedImage();
    if (currentCroppedImage) {
      try {
        // Convertir la imagen base64 a un archivo
        const arr = currentCroppedImage.split(',');
        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        // Crear un nuevo archivo con el mismo nombre que el original pero con el contenido recortado
        const currentFile = this.getFile();
        const fileName = currentFile ? currentFile.name : 'cropped-image.jpg';
        const croppedFile = new File([u8arr], fileName, { type: mime });

        // Emitir el archivo recortado
        this.croppedFile.emit(croppedFile);
        console.log('Archivo recortado emitido:', fileName);

        // Cerrar el modal con éxito
        this._popupService.close(this.modalId());
        this.closePopup.emit('success');
      } catch (error) {
        console.error('Error al procesar la imagen recortada:', error);
      }
    } else {
      console.warn('No hay imagen recortada disponible');
    }
  }

  /**
   * Cancela el proceso de recorte
   */
  cancel(): void {
    this.canceled.emit();
    this._popupService.close(this.modalId());
    this.closePopup.emit('close');
  }

  /**
   * Cierra el modal
   */
  closeAction(): void {
    this._popupService.close(this.modalId());
    this.closePopup.emit('close');
  }

  /**
   * Maneja la acción de cancelar desde el modal
   */
  cancelledAction(): void {
    this._popupService.close(this.modalId());
    this.closePopup.emit('close');
    this.canceled.emit();
  }

  /**
   * Controla el estado de carga del modal
   */
  setLoading(isLoading: boolean): void {
    this.loading.set(isLoading);
    if (isLoading) {
      this._spinnerService.show(this.modalId());
    } else {
      this._spinnerService.hide(this.modalId());
    }
  }
}
